import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

const STEPS = [
  {
    n: "01",
    title: "Inicijalna konzultacija",
    duration: "1 – 2 dana",
    desc: "Besplatno provjeravamo vašu situaciju — godinu izgradnje, status u prostornim planovima i ulazne pretpostavke za legalizaciju.",
    bullets: ["Pregled katastarskog plana", "Status u prostornim planovima", "Procjena izvedivosti"],
  },
  {
    n: "02",
    title: "Geodetska snimka izvedenog stanja",
    duration: "5 – 10 dana",
    desc: "Ovlašteni geodet izlazi na teren i radi snimku objekta s točnim dimenzijama i položajem u prostoru.",
    bullets: ["Terensko mjerenje", "Iscrtavanje situacije", "Snimka za potrebe legalizacije"],
  },
  {
    n: "03",
    title: "Arhitektonski snimak izvedenog stanja",
    duration: "7 – 14 dana",
    desc: "Ovlašteni arhitekt izrađuje tlocrte, presjeke i pročelja postojećeg objekta sukladno Pravilniku.",
    bullets: ["Tlocrti svih etaža", "Presjeci i pročelja", "Opis konstrukcije"],
  },
  {
    n: "04",
    title: "Predaja zahtjeva nadležnom tijelu",
    duration: "1 dan",
    desc: "Kompletiramo dokumentaciju, izračunavamo komunalni i vodni doprinos i naknadu, te predajemo zahtjev u upravni odjel.",
    bullets: ["Obrazac zahtjeva", "Sva potrebna dokumentacija", "Punomoć za zastupanje"],
  },
  {
    n: "05",
    title: "Postupak pred upravnim tijelom",
    duration: "30 – 90 dana",
    desc: "Zastupamo vas u cijelom upravnom postupku — odgovaramo na dopune, pribavljamo potvrde i pratimo predmet.",
    bullets: ["Komunikacija s referentom", "Dopune po potrebi", "Praćenje rokova"],
  },
  {
    n: "06",
    title: "Rješenje o izvedenom stanju",
    duration: "Nakon donošenja",
    desc: "Po izvršnosti rješenja provodimo upis u katastar i zemljišnu knjigu — objekt je legaliziran i evidentiran.",
    bullets: ["Izvršno rješenje", "Upis u katastar", "Upis u zemljišne knjige"],
  },
];

export default function PostupakPage() {
  return (
    <div data-testid="postupak-page">
      <Seo
        title="Postupak legalizacije u 6 koraka"
        description="Saznajte kako izgleda postupak legalizacije od inicijalne konzultacije do rješenja i upisa u katastar i zemljišne knjige."
        path="/postupak-legalizacije"
      />
      {/* HERO */}
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> Postupak
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                Postupak<br />legalizacije<br />
                <span className="text-ink/40">u 6 koraka.</span>
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              Cijeli postupak vodi naš tim — vi samo dostavite osnovne podatke o
              objektu i potpišete punomoć. Tipično trajanje: <span className="text-ink font-medium">30 dana</span>.
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="md:sticky md:top-24">
              <div className="overline text-ink-soft">Faze postupka</div>
              <p className="text-ink-soft text-sm mt-4 leading-relaxed">
                Klikom na “Prijavi se” javljamo se u roku od 24 sata s prvom
                procjenom izvedivosti i okvirnom ponudom.
              </p>
              <Link
                to="/prijava"
                data-testid="postupak-cta"
                className="inline-flex items-center gap-2 mt-6 bg-ink text-white px-5 py-3 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors"
              >
                Prijavi se
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </div>
          </div>

          <div className="md:col-span-9 relative">
            <div className="absolute left-0 top-0 bottom-0 w-px bg-ink/15 hidden md:block" />
            <ol className="space-y-0">
              {STEPS.map((s) => (
                <li
                  key={s.n}
                  className="relative md:pl-12 py-8 border-b border-hair last:border-b-0"
                  data-testid={`step-${s.n}`}
                >
                  <span className="hidden md:block absolute left-0 top-10 -translate-x-1/2 w-3 h-3 bg-ink rotate-45" />
                  <div className="grid grid-cols-12 gap-6 items-start">
                    <div className="col-span-12 md:col-span-2">
                      <div className="font-display text-5xl text-ink/15 font-bold leading-none">
                        {s.n}
                      </div>
                      <div className="overline text-ink-soft mt-3">{s.duration}</div>
                    </div>
                    <div className="col-span-12 md:col-span-10">
                      <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-ink">
                        {s.title}
                      </h3>
                      <p className="text-ink-soft mt-3 leading-relaxed max-w-2xl">
                        {s.desc}
                      </p>
                      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-2xl">
                        {s.bullets.map((b) => (
                          <li
                            key={b}
                            className="text-sm text-ink border border-hair px-3 py-2 bg-white"
                          >
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Bottom note */}
      <section className="border-t border-hair surface">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline text-ink-soft">Napomena</div>
            <p className="text-ink mt-3 leading-relaxed max-w-3xl">
              Rokovi i točan opseg dokumentacije ovise o lokaciji, namjeni i veličini
              objekta te kategoriji legalizacije. Detaljnu procjenu izrađujemo nakon
              uvida u dokumentaciju.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/zakonski-propisi"
              className="inline-flex items-center gap-2 border border-ink text-ink px-5 py-3 text-sm font-medium rounded-sm hover:bg-white transition-colors"
            >
              Zakonski propisi
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
